import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { MediaCard } from "@/components/MediaCard";
import { MediaLightbox } from "@/components/MediaLightbox";
import { api, fileUrl } from "@/lib/api";
import { VideoProcessing } from "@/components/VideoProcessing";
import { SmartCover, CoverAdjustButton } from "@/components/SmartCover";
import { useAuth } from "@/context/AuthContext";
import { ChevronRight, ChevronLeft, Layers, MapPin, Loader2, LayoutDashboard, Star, Users, Film, Globe, Dices, Image as ImageIcon } from "lucide-react";

const CONTENT_LIBS = ["originals", "celebrities", "pornstars", "web_finds", "community"];
const LIB_LABEL = { all: "All Libraries", originals: "BareFootage Originals", celebrities: "Celebrities", pornstars: "Pornstars", web_finds: "Best of the Web", community: "Community" };
const NAMED_LIBS = ["celebrities", "pornstars", "web_finds"];

const Cover = ({ thumb, type, poster, icon, frame }) => {
  // Video covers use the derived poster ONLY — never the raw .mov (?poster=1 on the original).
  if (type === "video") {
    return (
      <div className="aspect-square bg-black overflow-hidden relative">
        {poster ? <SmartCover src={poster} frame={frame} aspect={1} /> : <VideoProcessing compact />}
      </div>
    );
  }
  if (!thumb) {
    return (
      <div className="aspect-square bg-gradient-to-br from-secondary to-black flex items-center justify-center">
        {icon || <Layers className="w-7 h-7 text-muted-foreground" />}
      </div>
    );
  }
  return (
    <div className="aspect-square bg-black overflow-hidden">
      <SmartCover src={fileUrl(thumb) + "?w=600"} frame={frame} aspect={1} />
    </div>
  );
};

const GalleryTile = ({ testid, cover, coverType, coverPoster, coverMediaId, coverFrame, icon, title, sub, onClick, isAdmin, onFrameSaved }) => {
  const [frame, setFrame] = useState(coverFrame);
  useEffect(() => { setFrame(coverFrame); }, [coverFrame]);
  const src = coverType === "video" ? (coverPoster || undefined) : (cover ? fileUrl(cover) + "?w=600" : null);
  return (
    <div className="relative group">
      <button data-testid={testid} onClick={onClick} className="w-full text-left rounded-xl border border-border bg-card/40 overflow-hidden hover:border-primary/60 hover:-translate-y-0.5 transition-all">
        <div className="relative">
          <Cover thumb={cover} type={coverType} poster={coverPoster} icon={icon} frame={frame} />
          <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/80 to-transparent" />
        </div>
        <div className="p-2.5">
          <div className="text-sm font-heading capitalize truncate">{title}</div>
          <div className="text-[11px] text-muted-foreground">{sub}</div>
        </div>
      </button>
      {isAdmin && coverMediaId && src && (
        <CoverAdjustButton mediaId={coverMediaId} src={src} aspect={1} onSaved={(d) => { setFrame(d); onFrameSaved?.(d); }} />
      )}
    </div>
  );
};

const Galleries = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [tree, setTree] = useState([]);
  const [locTree, setLocTree] = useState([]);
  const [activeCat, setActiveCat] = useState(null);     // category object
  const [showLocation, setShowLocation] = useState(false);
  const [locPath, setLocPath] = useState([]);
  const [activeTag, setActiveTag] = useState(null);
  const [tagMedia, setTagMedia] = useState([]);
  const [loadingTag, setLoadingTag] = useState(false);
  const [selected, setSelected] = useState(null);
  const [library, setLibrary] = useState(() => {
    const l = searchParams.get("library");
    return CONTENT_LIBS.includes(l) ? l : "all";
  });
  const [subjects, setSubjects] = useState([]);
  const [activeSubject, setActiveSubject] = useState(null);
  const [libHome, setLibHome] = useState(null);   // Best of the Web header cards: Random / Photos / Videos
  useEffect(() => {
    setLibHome(null);
    if (library === "web_finds") api.get("/galleries/library-home", { params: { library } }).then((r) => setLibHome(r.data)).catch(() => setLibHome(null));
  }, [library]);
  const [mediaType, setMediaType] = useState(() => {
    const t = searchParams.get("type");
    return (t === "image" || t === "video") ? t : "all";
  });   // all | image | video

  const [treeLoading, setTreeLoading] = useState(true);
  const [treeError, setTreeError] = useState(false);
  const loadTree = useCallback(() => {
    setTreeLoading(true); setTreeError(false);
    const params = {};
    if (mediaType !== "all") params.media_type = mediaType;
    if (library !== "all") params.library = library;
    api.get("/galleries/tree", { params })
      .then((r) => setTree(r.data.tree || []))
      .catch(() => setTreeError(true))
      .finally(() => setTreeLoading(false));
  }, [mediaType, library]);
  useEffect(() => { loadTree(); }, [loadTree]);
  useEffect(() => {
    api.get("/galleries/location-tree").then((r) => setLocTree(r.data.tree || [])).catch(() => {});
  }, []);

  // Auto-populated named-subject galleries for Celebrities / Pornstars.
  useEffect(() => {
    setActiveSubject(null);
    if (NAMED_LIBS.includes(library)) {
      api.get("/library/subjects", { params: { library, ...(mediaType !== "all" ? { media_type: mediaType } : {}) } })
        .then((r) => setSubjects(r.data.subjects || [])).catch(() => setSubjects([]));
    } else { setSubjects([]); }
  }, [library, mediaType]);

  const openTag = useCallback(async (tag) => {
    setActiveTag(tag);
    setLoadingTag(true);
    try {
      const params = { tag, limit: 400 };
      if (mediaType !== "all") params.type = mediaType;
      if (library !== "all") params.library = library;
      const r = await api.get("/media", { params }); setTagMedia(r.data);
    }
    catch { setTagMedia([]); }
    setLoadingTag(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [mediaType, library]);

  const openSubject = useCallback(async (name) => {
    setActiveSubject(name);
    setLoadingTag(true);
    try {
      const params = { named_subject: name, library, limit: 400 };
      if (mediaType !== "all") params.type = mediaType;
      const r = await api.get("/media", { params }); setTagMedia(r.data);
    } catch { setTagMedia([]); }
    setLoadingTag(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [mediaType, library]);

  const switchLibrary = (lib) => { setLibrary(lib); setActiveCat(null); setActiveTag(null); setActiveSubject(null); };

  const didInitFromUrl = useRef(false);
  useEffect(() => {
    if (didInitFromUrl.current) return;   // only honour ?tag / ?cat once, so toggling type doesn't reopen a category
    const t = searchParams.get("tag");
    if (t) { openTag(t); didInitFromUrl.current = true; return; }
    const c = searchParams.get("cat");
    if (c && tree.length) { const found = tree.find((x) => x.category === c); if (found) { setActiveCat(found); didInitFromUrl.current = true; } }
  }, [searchParams, tree, openTag]);

  const currentLocNodes = locPath.length ? locPath[locPath.length - 1].children : locTree;
  const enterLoc = (node) => {
    if (node.level === "city" || !node.children || node.children.length === 0) navigate("/place/" + node.slug);
    else setLocPath((p) => [...p, node]);
  };

  // ---- Tag media view ----
  const AdminBar = () => user?.is_admin ? (
    <button data-testid="galleries-back-dashboard" onClick={() => navigate("/admin")}
      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary border border-border rounded-full px-3 py-1.5 mb-4 transition-colors">
      <LayoutDashboard className="w-3.5 h-3.5" /> Back to Dashboard
    </button>
  ) : null;

  if (activeTag || activeSubject) {
    const heading = activeSubject || activeTag;
    const libTag = library !== "all" ? LIB_LABEL[library] : null;
    return (
      <div className="min-h-screen relative z-10">
        <Navbar />
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-8">
          <div className="flex items-center gap-2 mb-4">
            <AdminBar />
            <button data-testid="gallery-back" onClick={() => { setActiveTag(null); setActiveSubject(null); setTagMedia([]); }} className="flex items-center gap-1.5 text-sm text-primary hover:underline mb-4"><ChevronLeft className="w-4 h-4" /> Back</button>
          </div>
          {libTag && <div className="text-[11px] uppercase tracking-widest text-primary mb-1" data-testid="tag-view-library">{libTag}</div>}
          <h1 className="font-heading text-3xl mb-1 capitalize">{heading}</h1>
          <p className="text-sm text-muted-foreground mb-6">{tagMedia.length} item{tagMedia.length === 1 ? "" : "s"}</p>
          {loadingTag ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            : tagMedia.length === 0 ? <p className="text-muted-foreground">Nothing here yet.</p>
            : <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">{tagMedia.map((m) => <MediaCard key={m.id} item={m} onOpen={() => setSelected(m)} />)}</div>}
        </div>
        {selected && <MediaLightbox mediaId={selected.id} open={!!selected} onClose={() => setSelected(null)} onChanged={() => openTag(activeTag)} playlist={tagMedia} />}
      </div>
    );
  }

  // ---- Inside a category: its tag galleries ----
  if (activeCat) {
    return (
      <div className="min-h-screen relative z-10">
        <Navbar />
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-8">
          <div className="flex items-center gap-2 mb-2"><AdminBar />
            <button data-testid="cat-back" onClick={() => setActiveCat(null)} className="flex items-center gap-1.5 text-sm text-primary hover:underline mb-4"><ChevronLeft className="w-4 h-4" /> All galleries</button>
          </div>
          <h1 className="font-heading text-3xl mb-1">{activeCat.category}</h1>
          <p className="text-sm text-muted-foreground mb-6">{activeCat.tags.length} galleries</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {activeCat.tags.map((t) => (
              <GalleryTile key={t.tag} testid={"gallery-tag-" + t.tag} cover={t.thumb} coverType={t.thumb_type} coverPoster={t.thumb_poster} coverMediaId={t.thumb_media_id} coverFrame={t.thumb_frame} isAdmin={!!user?.is_admin}
                title={t.tag} sub={t.count + " items"} onClick={() => openTag(t.tag)} />
            ))}
          </div>
        </div>
        {selected && <MediaLightbox mediaId={selected.id} open={!!selected} onClose={() => setSelected(null)} />}
      </div>
    );
  }

  // ---- Location drill-down ----
  if (showLocation) {
    return (
      <div className="min-h-screen relative z-10">
        <Navbar />
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-8">
          <div className="flex items-center gap-2 mb-2"><AdminBar />
            <button data-testid="loc-back" onClick={() => { setShowLocation(false); setLocPath([]); }} className="flex items-center gap-1.5 text-sm text-primary hover:underline mb-4"><ChevronLeft className="w-4 h-4" /> All galleries</button>
          </div>
          <h1 className="font-heading text-3xl mb-3">Location</h1>
          <div className="flex items-center flex-wrap gap-1 text-xs text-muted-foreground mb-4">
            <button onClick={() => setLocPath([])} className="hover:text-primary">World</button>
            {locPath.map((n, i) => (
              <span key={n.slug} className="flex items-center gap-1"><ChevronRight className="w-3 h-3 opacity-50" />
                <button onClick={() => setLocPath((p) => p.slice(0, i + 1))} className={i === locPath.length - 1 ? "text-primary" : "hover:text-primary"}>{n.name}</button></span>
            ))}
          </div>
          {(!currentLocNodes || currentLocNodes.length === 0) ? <p className="text-muted-foreground">No locations yet — add places to your photos and they'll show up here.</p>
            : <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">{currentLocNodes.map((n) => (
                <GalleryTile key={n.slug} testid={"loc-node-" + n.slug} cover={n.thumb} coverType={n.thumb_type} coverPoster={n.thumb_poster} coverMediaId={n.thumb_media_id} coverFrame={n.thumb_frame} isAdmin={!!user?.is_admin}
                  icon={<MapPin className="w-8 h-8 text-primary" />} title={n.name}
                  sub={(n.level === "city" ? "" : (n.children?.length || 0) + " places · ") + n.count} onClick={() => enterLoc(n)} />
              ))}</div>}
        </div>
      </div>
    );
  }

  // ---- Top level: category gallery cards (alphabetical) + Location ----
  const cards = [...tree].filter((c) => library === "all" || (c.count || 0) > 0).map((c) => ({ ...c, _key: c.category }));   // inside one library, hide categories with nothing in it
  const locCount = locTree.reduce((s, n) => s + (n.count || 0), 0);
  // Location is ONLY part of the Originals experience (canonical dataset = Originals + manual candid) — never appended to other libraries.
  const locationCardVisible = library === "originals" && locCount > 0;
  const merged = [...cards, ...(locationCardVisible ? [{ _key: "Location", isLocation: true, count: locCount }] : [])].sort((a, b) => a._key.localeCompare(b._key));

  return (
    <div className="min-h-screen relative z-10">
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-8">
        <AdminBar />
        <h1 className="font-gothic italic text-4xl sm:text-5xl mb-2">Galleries</h1>
        <p className="text-muted-foreground mb-5 text-base">Every category is a gallery. Open one to browse its collections.</p>
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="galleries-library-tabs">
          {["all", ...CONTENT_LIBS].map((lib) => (
            <button key={lib} data-testid={`galleries-lib-${lib}`} onClick={() => switchLibrary(lib)}
              className={"px-4 py-1.5 rounded-full text-sm border transition-colors " + (library === lib ? "bg-primary text-primary-foreground border-primary font-medium" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40")}>
              {LIB_LABEL[lib]}
            </button>
          ))}
        </div>
        <div className="inline-flex items-center gap-1 p-1 rounded-full bg-secondary/40 border border-border mb-7" data-testid="galleries-type-toggle">
          {[["all", "All"], ["image", "Photos"], ["video", "Videos"]].map(([v, label]) => (
            <button key={v} data-testid={`galleries-type-${v}`} onClick={() => { setMediaType(v); setActiveCat(null); }}
              className={"px-4 py-1.5 rounded-full text-sm transition-colors " + (mediaType === v ? "bg-primary text-primary-foreground font-medium" : "text-muted-foreground hover:text-foreground")}>
              {label}
            </button>
          ))}
        </div>
        {library === "web_finds" && libHome && (
          <div className="mb-8" data-testid="web-finds-home">
            <h2 className="font-heading text-xl mb-3 flex items-center gap-2"><Globe className="w-5 h-5 text-primary" /> Best of the Web</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {[["random", "Random", Dices, libHome.random, ""], ["photos", "Photos", ImageIcon, libHome.photos, "?type=image"], ["videos", "Videos", Film, libHome.videos, "?type=video"]].filter(([k, , , d]) => k === "random" || (d?.count || 0) > 0).map(([k, label, Icon, d, qs]) => (
                <GalleryTile key={k} testid={`web-finds-${k}`} cover={d?.cover?.file_ref} coverType={d?.cover?.media_type} coverPoster={d?.cover?.poster} coverMediaId={d?.cover?.id} coverFrame={d?.cover?.frame} isAdmin={!!user?.is_admin}
                  icon={<Icon className="w-8 h-8 text-primary" />} title={label} sub={(d?.count || 0) + (d?.count === 1 ? " item" : " items")}
                  onClick={() => libHome.random_collection_id ? navigate(`/collection/${libHome.random_collection_id}${qs}`) : null} />
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">Internet finds, kept separate from Originals, Celebrities, Pornstars and Community. Every category below is scoped to this library only.</p>
          </div>
        )}
        {NAMED_LIBS.includes(library) && (library !== "web_finds" || subjects.length > 0) && (
          <div className="mb-8" data-testid="named-subject-galleries">
            <h2 className="font-heading text-xl mb-3 flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> {library === "pornstars" ? "Performers" : library === "web_finds" ? "Named Subjects" : "Named Celebrities"}</h2>
            {subjects.length === 0 ? <p className="text-sm text-muted-foreground mb-2">No named subjects in this library yet.</p>
              : <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {subjects.map((s) => (
                    <GalleryTile key={s.name} testid={"subject-" + s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")} cover={s.thumb} coverType={s.thumb_type} coverPoster={s.thumb_poster} coverMediaId={s.thumb_media_id} coverFrame={s.thumb_frame} isAdmin={!!user?.is_admin}
                      icon={<Users className="w-8 h-8 text-primary" />} title={s.name} sub={s.count + (s.count === 1 ? " item" : " items")} onClick={() => openSubject(s.name)} />
                  ))}
                </div>}
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="category-cards">
          {merged.map((c) => c.isLocation ? (
            <GalleryTile key="Location" testid="gallery-cat-Location" icon={<MapPin className="w-8 h-8 text-primary" />}
              title="Location" sub={c.count + " placed"} onClick={() => { setShowLocation(true); setLocPath([]); }} />
          ) : (
            <GalleryTile key={c.category} testid={"gallery-cat-" + c.category} cover={c.cover} coverType={c.cover_type} coverPoster={c.cover_poster} coverMediaId={c.cover_media_id} coverFrame={c.cover_frame} isAdmin={!!user?.is_admin}
              title={c.category} sub={c.tags.length + " galleries · " + c.count} onClick={() => setActiveCat(c)} />
          ))}
        </div>
        {treeError && tree.length === 0 && (
          <div className="flex items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-4 mt-2" data-testid="galleries-load-error">
            <span className="text-sm text-destructive">Couldn't load galleries. Please try again.</span>
            <button data-testid="galleries-retry" onClick={loadTree} className="text-sm px-3 py-1.5 rounded-full border border-border hover:border-primary/60 transition-colors">Retry</button>
          </div>
        )}
        {treeLoading && tree.length === 0 && !treeError && (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        )}
      </div>
      {selected && <MediaLightbox mediaId={selected.id} open={!!selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default Galleries;
